type DataReaderState<T> =
  | {
      status: 'idle';
      value: undefined;
    }
  | {
      status: 'pending';
      value: Promise<void>;
    }
  | {
      status: 'rejected';
      value: Error;
    }
  | {
      status: 'resolved';
      value: T;
    };

type DataReaderOptions = {
  /**
   * Prevent eager loading, data will begin to load when it’s being read for the first time
   */
  defer?: boolean;
};

type DataReader<T> = {
  /**
   * Read data synchronously
   *
   * @throws {Promise<void>} A promise if data is still loading (according to Suspense spec)
   * @throws {Error} If underlying promise rejected
   */
  read: () => T;
};

/**
 * Create Suspense-ready data reader
 *
 * Allows to read async data synchronously inside React component and take
 * advantage of Suspense and Error boundaries for loading/exception handling
 *
 * @param fetcher Factory that returns promise eventually resolved with desired data
 * @param options Data reader configuration
 */
export function createDataReader<T>(fetcher: () => Promise<T>, options?: DataReaderOptions): DataReader<T> {
  let cache: DataReaderState<T> = {
    status: 'idle',
    value: undefined,
  };

  function init() {
    const promise = fetcher()
      .then((value) => {
        cache = {
          status: 'resolved',
          value,
        };
      })
      .catch((error) => {
        const coercedError = error instanceof Error ? error : new Error('Unknown error');
        cache = {
          status: 'rejected',
          value: coercedError,
        };
      });

    cache = {
      status: 'pending',
      value: promise,
    };

    return promise;
  }

  if (options?.defer !== true) {
    void init();
  }

  return {
    read() {
      switch (cache.status) {
        case 'idle': {
          throw init();
        }
        case 'pending': {
          throw cache.value;
        }
        case 'rejected': {
          throw cache.value;
        }
        case 'resolved': {
          return cache.value;
        }
        default: {
          throw new Error('Unknown error');
        }
      }
    },
  };
}
